import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from 'redux/user/selectors';
import {
  addBackgroundTask,
  reloadBackgroundTasks,
  removeBackgroundTask,
  syncBackgroundTasks,
  updateBackgroundTask,
} from 'redux/backgroundTasks';
import { setDataManagementRefresh } from 'redux/dataManagement';
import { TypedDispatch } from 'redux/configureStore';
import { AsyncJob, AsyncJobStatus, AsyncJobType } from 'types';
import { showNotification } from 'common/function';
import { parseSseFrame } from 'service/sseFrames';
import { getConfigTestCopyText } from 'components/UserSettings/HRIS/WorkdayRaaS';
import { useSseStream } from 'hooks/useSseStream';

// The notifier service is same-origin through CloudFront; only the local dev
// server (app on :3006, notifier on :8999) needs the explicit endpoint.
const NOTIFIER_BASE_URL = process.env.NEXT_PUBLIC_NOTIFIER_ENDPOINT || '/ws';
export const BACKGROUND_TASKS_STREAM_URL = `${NOTIFIER_BASE_URL}/stream`;
const BACKGROUND_TASKS_FALLBACK_PERIOD_MS = 60_000;

/**
 * Wire format of one notifier frame: `type` and `payload` exactly as the
 * producer put them on the queue. Producers: api asyncJobScheduler
 * (JOB_CREATED / JOB_UPDATED), composite-calculation (JOB_UPDATED),
 * data-management (DATA_MANAGEMENT, payload is a JSON string).
 */
type BackgroundTasksFrame =
  | { type: 'CONNECTED' }
  | { type: 'JOB_CREATED' | 'JOB_UPDATED'; payload: AsyncJob }
  | { type: 'DATA_MANAGEMENT'; payload: string };

const isFinished = (job: AsyncJob): boolean =>
  job.status === AsyncJobStatus.DONE || job.status === AsyncJobStatus.FAILED;

const toastHrisTestResult = (job: AsyncJob): void => {
  if (job.type !== AsyncJobType.TEST_HRIS_URL) {
    return;
  }
  const result = job.result ?? {};
  if (result.hrisErrorMessage || result.error) {
    showNotification(result.hrisErrorMessage || 'Unknown error occurred', 'HRIS Test Failed', 'error');
    return;
  }
  const configName = job.details?.configName || 'HRIS';
  showNotification(`${getConfigTestCopyText(configName)} test completed successfully`, 'HRIS Test Success', 'success');
};

/**
 * Applies one frame to the store. CONNECTED triggers the authoritative REST
 * snapshot (there is no server-side initial state), so a (re)connect always
 * reconciles whatever was missed while the stream was down.
 */
const handleBackgroundTasksFrame = (dispatch: TypedDispatch, data: string): void => {
  const frame = parseSseFrame<BackgroundTasksFrame>('background-tasks', data);
  if (!frame) {
    return;
  }

  switch (frame.type) {
    case 'CONNECTED':
      dispatch(syncBackgroundTasks());
      break;
    case 'JOB_CREATED':
      dispatch(addBackgroundTask(frame.payload));
      break;
    case 'JOB_UPDATED':
      if (isFinished(frame.payload)) {
        toastHrisTestResult(frame.payload);
        dispatch(removeBackgroundTask(frame.payload));
      } else {
        dispatch(updateBackgroundTask(frame.payload));
      }
      break;
    case 'DATA_MANAGEMENT':
      dispatch(setDataManagementRefresh(frame.payload));
      break;
    default:
      break;
  }
};

/**
 * Keeps the background-tasks badge and the Data Management status table live:
 * one authenticated SSE stream from the notifier per signed-in user, re-keyed
 * on the tenant so an in-place switch reconnects with the new workspace's
 * token, plus a 60 s REST fallback that prunes tasks that finished while a
 * frame was missed. The stream stays open in hidden tabs on purpose: a missed
 * DATA_MANAGEMENT frame would leave the status table stale. Mount once per
 * auth shell (LegacyApp / AuthedAppServices).
 */
export const useBackgroundTasksStream = (): void => {
  const user = useSelector(selectUser);
  const dispatch = useDispatch<TypedDispatch>();
  const userId = user?.id;
  const companyId = user?.companyId;

  useSseStream({
    url: userId ? BACKGROUND_TASKS_STREAM_URL : null,
    reconnectKey: `${userId}:${companyId}`,
    onMessage: (data) => handleBackgroundTasksFrame(dispatch, data),
  });

  useEffect(() => {
    if (!userId) {
      return undefined;
    }
    const fallbackReload = setInterval(() => {
      dispatch(reloadBackgroundTasks());
    }, BACKGROUND_TASKS_FALLBACK_PERIOD_MS);
    return () => clearInterval(fallbackReload);
  }, [userId, dispatch]);
};
